import { MinusIcon, PlusIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import ArrowExpandIcon from '../../../assets/icons/arrow-expand.svg?react';
import FitToScreenIcon from '../../../assets/icons/fit-to-screen.svg?react';
import RotateIcon from '../../../assets/icons/rotate-double.svg?react';
import { useScreenTools } from '../../../hooks/potree/useScreenTools';
import { Project } from '../../../types/graphqlTypes';
import classNames from 'classnames';
import { memo, useCallback, useContext, useMemo } from 'react';
import { IconBox } from '../../../components/IconBox';
import { Tooltip } from '../../../components/Tooltip';
import { T } from '../../../translation/src';
import { ViewerContext } from '../../../contexts/ViewerContext';
import { useToggleHide } from '../../../hooks/modules/project/useToggleHide';
import { RendererContext } from '../../../contexts/RendererContext';
import { ShortcutsModal } from '../../../components/Modal/ShortcutsModal';
import useOpen from '../../../hooks/useOpen';
import { WMSLayersContext } from '../../../contexts/WmsLayersContext';

interface ScreenToolsProps {
  project?: Pick<Project, 'id' | 'mapVisible' | 'settings'>;
}

const MapTool_: React.FC2<ScreenToolsProps> = ({ className, project, ...props }) => {
  const { in2DMode, enable2DMode, disable2DMode } = useContext(ViewerContext);
  const { toggleHide } = useToggleHide();
  const wmsLayersContextValue = useContext(WMSLayersContext);
  const wmsLayers = useMemo(() => wmsLayersContextValue.wmsLayers, [wmsLayersContextValue.wmsLayers]);

  const on2D = useCallback(() => {
    enable2DMode('map');
  }, [enable2DMode]);

  const on3D = useCallback(() => {
    if (in2DMode) {
      toggleHide({ identifiers: ['map'].concat(wmsLayers.map((layer) => layer.identifier)), visible: false });
      disable2DMode();
    }
  }, [disable2DMode, in2DMode, toggleHide, wmsLayers]);

  return (
    <div className={classNames('flex divide-x-2 divide-gray-700 z-10 w-auto', className)} {...props}>
      <IconBox text={'2D'} highlight grayedOut={!in2DMode} onClick={on2D} className="rounded-l-lg" />

      <IconBox text={'3D'} highlight grayedOut={in2DMode} onClick={on3D} className="rounded-r-lg" />
    </div>
  );
};

const ScreenTools_: React.FC2<ScreenToolsProps> = ({ className, project, ...props }) => {
  const [{ fitToScreen, onFullScreen, flip2DView }, { onZoomInStart, onZoomOutStart, onZoomEnd }] = useScreenTools({
    project,
  });
  const renderer = useContext(RendererContext);
  const viewer = renderer.viewer;
  return (
    <div className={classNames('flex divide-x-2 divide-gray-700 z-10 w-auto', className)} {...props}>
      {/* <IconBox icon={CubeIcon} */}
      {viewer?.viewerMode.hasVirtualPlane && (
        <Tooltip message={<T _str="flip the 2D view" swc />} delay={0}>
          <IconBox icon={RotateIcon} onClick={flip2DView} highlight className="rounded-l-lg" />
        </Tooltip>
      )}
      {document.fullscreenEnabled && (
        <Tooltip message={<T _str="expand" swc />} delay={0}>
          <IconBox
            icon={ArrowExpandIcon}
            onClick={onFullScreen}
            highlight
            className={!viewer?.viewerMode.hasVirtualPlane ? 'rounded-l-lg' : ''}
          />
        </Tooltip>
      )}
      <Tooltip message={<T _str="fit to screen" swc />} delay={0}>
        <IconBox icon={FitToScreenIcon} onClick={fitToScreen} highlight />
      </Tooltip>
      <Tooltip message={<T _str="zoom out" swc />} delay={0}>
        <IconBox icon={MinusIcon} onMouseDown={onZoomOutStart} onMouseUp={onZoomEnd} highlight />
      </Tooltip>
      <Tooltip message={<T _str="zoom in" swc />} delay={0}>
        <IconBox icon={PlusIcon} onMouseDown={onZoomInStart} onMouseUp={onZoomEnd} highlight className="rounded-r-lg" />
      </Tooltip>
    </div>
  );
};

const Shortcuts_: React.FC2<ScreenToolsProps> = ({ className, project, ...props }) => {
  const { open, onClose, onOpen } = useOpen();

  const polylinePolygonShortcuts = useMemo(() => {
    return [
      { description: <T _str="add new point" swc />, shortcuts: [<T _str="click Left" swc />] },
      { description: <T _str="remove last point" swc />, shortcuts: [<T _str="click Right" swc />] },
      { description: <T _str="snap angles" swc />, shortcuts: ['⇧'] },
      {
        description: <T _str="complete shape" swc />,
        shortcuts: [<T _str="double click" swc />, <T _str="⏎" swc />],
      },
    ];
  }, []);

  const shortcuts = useMemo(
    () => [
      {
        group: 'navigation' as const,
        shortcuts: [
          { description: <T _str="zoom to cursor position" swc />, shortcuts: [<T _str="double click" swc />] },
          {
            description: <T _str="orbit camera" swc />,
            shortcuts: [<T _str="click Left" swc />, <T _str="drag" swc />],
          },
          { description: <T _str="pan view" swc />, shortcuts: [<T _str="click Right" swc />, <T _str="drag" swc />] },
          { description: <T _str="zoom in/out" swc />, shortcuts: [<T _str="mouse wheel" swc />] },
        ],
      },
      {
        group: 'line' as const,
        shortcuts: polylinePolygonShortcuts,
      },
      {
        group: 'polygon' as const,
        shortcuts: polylinePolygonShortcuts,
      },
      {
        group: 'plane' as const,
        shortcuts: [
          { description: <T _str="snap to horizontal/vertical" swc />, shortcuts: ['⇧'] },
          { description: <T _str="refine best-fit plane" swc />, shortcuts: [<T _str="click left" swc />] },
          { description: <T _str="undo last cursor position" swc />, shortcuts: [<T _str="click right" swc />] },
          {
            description: <T _str="create plane" swc />,
            shortcuts: [<T _str="double click" swc />, <T _str="⏎" swc />],
          },
        ],
      },
      {
        group: 'floorplan' as const,
        shortcuts: [
          { description: <T _str="create perpendicular angles" swc />, shortcuts: ['⇧'] },
          { description: <T _str="refine best-fit plane" swc />, shortcuts: ['Ctrl'] },
          { description: <T _str="change base plane" swc />, shortcuts: [<T _str="alt" swc />] },
          { description: <T _str="add new segment" swc />, shortcuts: [<T _str="click left" swc />] },
          { description: <T _str="remove last segment" swc />, shortcuts: [<T _str="click right" swc />] },
          { description: <T _str="create object" swc />, shortcuts: [<T _str="double click" swc />] },
        ],
      },
      {
        group: 'magic wand' as const,
        shortcuts: [
          { description: <T _str="add detected object" swc />, shortcuts: [<T _str="click Left" swc />] },
          { description: <T _str="remove object" swc />, shortcuts: [<T _str="click Right" swc />] },
          {
            description: <T _str="create cluster" swc />,
            shortcuts: [<T _str="double click" swc />, <T _str="⏎" swc />],
          },
          { description: <T _str="add all in lasso" swc />, shortcuts: ['Ctrl'] },
        ],
      },
    ],
    [polylinePolygonShortcuts],
  );

  return (
    <>
      <div className={classNames('flex divide-x-2 divide-gray-700 z-10 w-auto', className)} {...props}>
        <Tooltip message={<T _str="shortcuts" swc />} delay={0}>
          <IconBox icon={LightBulbIcon} onClick={onOpen} className="rounded-lg" highlight />
        </Tooltip>
      </div>
      <ShortcutsModal open={open} onClose={onClose} shortcuts={shortcuts} />
    </>
  );
};

export const ScreenTools = memo(ScreenTools_);
export const MapTool = memo(MapTool_);
export const Shortcuts = memo(Shortcuts_);
